import {
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks
} from 'date-fns'
import Fuse from 'fuse.js'
import type { Workout } from '../types'
import { exerciseProcessor } from '../services/exerciseProcessor'

// Simple date filter type
interface DateFilter {
  start: Date
  end: Date
}

// Searchable workout structure for Fuse.js
interface SearchableWorkout extends Workout {
  searchableExercises: string
  searchableMuscleGroups: string
  searchableRoutine: string
  searchableTags: string
}

/**
 * Extract date filter from query
 * Returns the date filter and remaining query text
 */
function extractDateFilter(query: string): {
  dateFilter: DateFilter | null
  remainingQuery: string
} {
  const lowerQuery = query.toLowerCase()
  const now = new Date()

  // Date patterns to check
  const patterns: Array<{ regex: RegExp; handler: () => DateFilter }> = [
    {
      regex: /\btoday\b/,
      handler: () => ({ start: now, end: now })
    },
    {
      regex: /\byesterday\b/,
      handler: () => {
        const yesterday = subDays(now, 1)
        return { start: yesterday, end: yesterday }
      }
    },
    {
      regex: /(\d+)\s+days?\s+ago/,
      handler: () => {
        const match = lowerQuery.match(/(\d+)\s+days?\s+ago/)
        const days = parseInt(match![1])
        const targetDate = subDays(now, days)
        return { start: targetDate, end: targetDate }
      }
    },
    {
      regex: /\bthis\s+week\b/,
      handler: () => ({ start: startOfWeek(now), end: endOfWeek(now) })
    },
    {
      regex: /\blast\s+week\b/,
      handler: () => {
        const lastWeek = subWeeks(now, 1)
        return { start: startOfWeek(lastWeek), end: endOfWeek(lastWeek) }
      }
    },
    {
      regex: /\bthis\s+month\b/,
      handler: () => ({ start: startOfMonth(now), end: endOfMonth(now) })
    },
    {
      regex: /\blast\s+month\b/,
      handler: () => {
        const lastMonth = subMonths(now, 1)
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) }
      }
    },
    {
      regex: /\blast\s+(\d+)\s+days?\b/,
      handler: () => {
        const match = lowerQuery.match(/\blast\s+(\d+)\s+days?\b/)
        const days = parseInt(match![1])
        return { start: subDays(now, days), end: now }
      }
    },
    // Month names
    {
      regex:
        /\b(january|february|march|april|may|june|july|august|september|october|november|december)\b/,
      handler: () => {
        const monthNames = [
          'january',
          'february',
          'march',
          'april',
          'may',
          'june',
          'july',
          'august',
          'september',
          'october',
          'november',
          'december'
        ]
        const match = lowerQuery.match(
          /\b(january|february|march|april|may|june|july|august|september|october|november|december)\b/
        )
        const monthIndex = monthNames.indexOf(match![1])
        const year = now.getFullYear()
        const monthStart = new Date(year, monthIndex, 1)

        // If the month is in the future, assume last year
        if (monthStart > now) {
          monthStart.setFullYear(year - 1)
        }

        return {
          start: monthStart,
          end: endOfMonth(monthStart)
        }
      }
    }
  ]

  // Check each pattern
  for (const { regex, handler } of patterns) {
    if (regex.test(lowerQuery)) {
      const dateFilter = handler()
      // Remove the date pattern from the query
      const remainingQuery = lowerQuery.replace(regex, '').trim()
      return { dateFilter, remainingQuery }
    }
  }

  // No date filter found
  return { dateFilter: null, remainingQuery: query }
}

/**
 * Transform workout into searchable format
 */
function createSearchableWorkout(workout: Workout, workoutTag?: string): SearchableWorkout {
  // Extract all exercise names
  const exercises = workout.exercises?.map((e) => e.title) || []

  // Extract all muscle groups using new exercise processor
  const muscleGroups = new Set<string>()
  workout.exercises?.forEach((exercise) => {
    // Use processed exercise data if available, otherwise process on-the-fly
    if (exercise.primaryMover) {
      muscleGroups.add(exercise.primaryMover)
      exercise.secondaryMovers?.forEach((group) => muscleGroups.add(group))
    } else {
      // Process exercise if muscle group data is missing
      const processed = exerciseProcessor.processExercise({
        templateId: exercise.exercise_template_id,
        name: exercise.title
      })
      muscleGroups.add(processed.primaryMover)
      processed.secondaryMovers.forEach((group) => muscleGroups.add(group))
    }
  })

  return {
    ...workout,
    searchableExercises: exercises.join(' '),
    searchableMuscleGroups: Array.from(muscleGroups).join(' '),
    searchableRoutine: workout.title || '',
    searchableTags: workoutTag || ''
  }
}

/**
 * Search workouts using fuzzy search
 */
export function searchWorkouts(
  query: string,
  workouts: Workout[],
  workoutTags?: Record<string, string>
): Workout[] {
  if (!query.trim()) return workouts

  // 1. Extract date filter
  const { dateFilter, remainingQuery } = extractDateFilter(query)

  // 2. Filter by date if needed
  let filteredWorkouts = workouts
  if (dateFilter) {
    filteredWorkouts = workouts.filter((workout) => {
      const workoutDate = new Date(workout.start_time)
      // Set time to start/end of day for proper comparison
      const startOfDay = new Date(dateFilter.start)
      startOfDay.setHours(0, 0, 0, 0)
      const endOfDay = new Date(dateFilter.end)
      endOfDay.setHours(23, 59, 59, 999)

      return workoutDate >= startOfDay && workoutDate <= endOfDay
    })
  }

  // 3. If no remaining query, return date-filtered results
  if (!remainingQuery.trim()) {
    return filteredWorkouts
  }

  // 4. Create searchable workouts
  const searchableWorkouts = filteredWorkouts.map((workout) =>
    createSearchableWorkout(workout, workoutTags?.[workout.id])
  )

  // 5. Create a combined searchable text for each workout
  const workoutsWithCombinedText = searchableWorkouts.map((workout) => ({
    ...workout,
    combinedSearchText: [
      workout.searchableRoutine,
      workout.searchableExercises,
      workout.searchableMuscleGroups,
      workout.searchableTags
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
  }))

  // 6. Split query into words for better multi-word matching
  const queryWords = remainingQuery
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 1)

  // 7. If multiple words, search for each word and combine results
  if (queryWords.length > 1) {
    // Score each workout based on how many query words it contains
    const scoredWorkouts = workoutsWithCombinedText.map((workout) => {
      let matches = 0
      let totalScore = 0

      queryWords.forEach((word) => {
        if (workout.combinedSearchText.includes(word)) {
          matches++
          // Give higher weight to routine name matches
          if (workout.searchableRoutine.toLowerCase().includes(word)) {
            totalScore += 2
          } else {
            totalScore += 1
          }
        }
      })

      return {
        workout,
        matches,
        score: matches > 0 ? totalScore / queryWords.length : 0
      }
    })

    // Filter to only workouts that match at least one word
    const matchingWorkouts = scoredWorkouts
      .filter((item) => item.matches > 0)
      .sort((a, b) => b.score - a.score)

    return matchingWorkouts.map((item) => {
      const {
        searchableExercises: _searchableExercises,
        searchableMuscleGroups: _searchableMuscleGroups,
        searchableRoutine: _searchableRoutine,
        searchableTags: _searchableTags,
        combinedSearchText: _combinedSearchText,
        ...clean
      } = item.workout
      return clean as Workout
    })
  }

  // 8. For single word queries, use fuzzy search
  const fuse = new Fuse(workoutsWithCombinedText, {
    keys: ['combinedSearchText'],
    threshold: 0.4, // More forgiving for single words
    includeScore: true,
    ignoreLocation: true,
    minMatchCharLength: 2,
    shouldSort: true
  })

  // 9. Search and return results
  const results = fuse.search(remainingQuery.toLowerCase())

  // Return results sorted by relevance
  return results.map((result) => {
    // Remove the searchable fields before returning
    const {
      searchableExercises: _searchableExercises,
      searchableMuscleGroups: _searchableMuscleGroups,
      searchableRoutine: _searchableRoutine,
      searchableTags: _searchableTags,
      combinedSearchText: _combinedSearchText,
      ...workout
    } = result.item
    return workout as Workout
  })
}
